import { Box, Typography, IconButton, LinearProgress } from "@mui/material";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { usePanelTokens } from "../../../../utils/panelTokens";

const DocTOC = ({ steps, activeId, onVideoClick, onClose }) => {
  const T = usePanelTokens();
  const activeIndex = steps.findIndex((s) => s.id === activeId);
  const progressPercent = steps.length
    ? Math.round(((activeIndex + 1) / steps.length) * 100)
    : 0;

  const scrollToStep = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToVideo = () => {
    const el = document.getElementById("doc-video-section");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    if (onVideoClick) onVideoClick();
  };

  return (
    <Box
      sx={{
        width: 252,
        flexShrink: 0,
        position: "sticky",
        top: 16,
        maxHeight: "calc(100vh - 96px)",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        overflow: "hidden",
        border: `1px solid ${T.border}`,
        backgroundColor: T.surface,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${T.border}`,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: T.textMuted,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          Table of contents
        </Typography>
        {onClose && (
          <IconButton size="small" onClick={onClose} sx={{ ml: 1, p: 0.5 }}>
            <CloseIcon sx={{ fontSize: 16, color: T.textMuted }} />
          </IconButton>
        )}
      </Box>

      {/* Progress bar */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${T.borderSoft}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
          <Typography sx={{ fontSize: 10, color: T.textFaint, fontWeight: 500 }}>
            Progress
          </Typography>
          <Typography
            sx={{
              fontSize: 10,
              color: progressPercent === 100 ? T.success : T.accent,
              fontWeight: 600,
            }}
          >
            {progressPercent}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 3,
            borderRadius: 2,
            backgroundColor: T.track,
            "& .MuiLinearProgress-bar": {
              borderRadius: 2,
              background:
                progressPercent === 100
                  ? `linear-gradient(to right, ${T.success}, ${T.successAlt})`
                  : `linear-gradient(to right, ${T.accent}, ${T.accentHover})`,
            },
          }}
        />
      </Box>

      {/* Scrollable list */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          py: 1,
          px: 1,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: T.scrollThumb,
            borderRadius: 2,
          },
        }}
      >
        {/* Video item */}
        <Box
          onClick={scrollToVideo}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 1.25,
            py: 1,
            borderRadius: 1.5,
            cursor: "pointer",
            mb: 0.5,
            "&:hover": { backgroundColor: T.hoverSoft },
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: `1.5px solid ${T.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PlayCircleFilledIcon sx={{ fontSize: 14, color: T.textMuted }} />
          </Box>
          <Typography sx={{ fontSize: 13, color: T.text, fontWeight: 500 }}>
            Video
          </Typography>
        </Box>

        <Box sx={{ height: "1px", backgroundColor: T.borderSoft, mx: 1, my: 0.75 }} />

        {/* Steps */}
        {steps.map((step, index) => {
          const isActive = activeId === step.id;
          const isPast = index < activeIndex;

          return (
            <Box
              key={step.id}
              onClick={() => scrollToStep(step.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 1.25,
                py: 0.875,
                borderRadius: 1.5,
                cursor: "pointer",
                mb: 0.25,
                position: "relative",
                backgroundColor: isActive ? "rgba(58,142,255,0.12)" : "transparent",
                "&:hover": {
                  backgroundColor: isActive
                    ? "rgba(58,142,255,0.16)"
                    : T.hoverSoft,
                },
                "&::before": isActive
                  ? {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: "20%",
                      bottom: "20%",
                      width: 3,
                      borderRadius: "0 2px 2px 0",
                      backgroundColor: T.accent,
                    }
                  : {},
              }}
            >
              {/* Step badge */}
              <Box
                sx={{
                  minWidth: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  backgroundColor: isActive
                    ? T.accent
                    : isPast
                    ? "rgba(34,197,94,0.15)"
                    : T.track,
                  border: isActive
                    ? "1.5px solid #3a8eff"
                    : isPast
                    ? "1.5px solid rgba(34,197,94,0.3)"
                    : `1.5px solid ${T.border}`,
                }}
              >
                {isPast && !isActive ? (
                  <CheckCircleIcon sx={{ fontSize: 14, color: T.success }} />
                ) : (
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: isActive ? "#fff" : T.textMuted,
                      lineHeight: 1,
                    }}
                  >
                    {String(step.stepNumber).padStart(2, "0")}
                  </Typography>
                )}
              </Box>

              <Typography
                sx={{
                  fontSize: 12.5,
                  color: isActive ? T.accent : isPast ? T.textMuted : T.text,
                  fontWeight: isActive ? 600 : 400,
                  lineHeight: 1.4,
                }}
              >
                {step.title}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderTop: `1px solid ${T.borderSoft}`,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{ fontSize: 10, color: T.textFaint, textAlign: "center", letterSpacing: "0.02em" }}
        >
          {activeIndex + 1} of {steps.length} steps
        </Typography>
      </Box>
    </Box>
  );
};

export default DocTOC;
